// Concrete LLM providers — OpenAI, Anthropic, Gemini.
//
// Each translates the provider-agnostic ChatRequest into its own wire
// format and parses the response back. CORS is supported by all three
// for direct browser calls; the API key is sent from the user's browser
// straight to the provider — there's no intermediary in this POC.
//
// Production note: a static SPA holding API keys in localStorage is a
// demo pattern. Production deployments should proxy chat traffic
// through an Azure backend (Function / Container App) with managed
// identity, and key rotation via Key Vault.

import type {
  ChatRequest,
  ChatResponse,
  LlmProvider,
  LlmProviderId,
  LlmSettings,
} from "./types";
import { LlmError } from "./types";

const DEFAULT_TEMPERATURE = 0.3;
const DEFAULT_MAX_TOKENS = 1500;

// ---------------------------------------------------------------------------
// OpenAI — Chat Completions API
// ---------------------------------------------------------------------------

const openai: LlmProvider = {
  id: "openai",
  label: "OpenAI",
  defaultModel: "gpt-4o-mini",
  modelHints: ["gpt-4o-mini", "gpt-4o", "gpt-4.1", "gpt-4.1-mini", "o3-mini"],

  async complete(req: ChatRequest, settings: LlmSettings): Promise<ChatResponse> {
    const messages = [
      { role: "system", content: req.system },
      ...req.messages.map((m) => ({ role: m.role, content: m.content })),
    ];

    const res = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${settings.apiKey}`,
      },
      body: JSON.stringify({
        model: settings.model,
        messages,
        temperature: settings.temperature ?? DEFAULT_TEMPERATURE,
        max_tokens: settings.maxTokens ?? DEFAULT_MAX_TOKENS,
      }),
    });

    if (!res.ok) {
      const body = await res.text();
      throw new LlmError(`OpenAI ${res.status}: ${body.slice(0, 200)}`, res.status);
    }

    const data = await res.json();
    const content: string = data?.choices?.[0]?.message?.content ?? "";
    return {
      content,
      model: data?.model ?? settings.model,
      usage: {
        inputTokens: data?.usage?.prompt_tokens,
        outputTokens: data?.usage?.completion_tokens,
      },
    };
  },
};

// ---------------------------------------------------------------------------
// Anthropic — Messages API
// ---------------------------------------------------------------------------

const anthropic: LlmProvider = {
  id: "anthropic",
  label: "Anthropic (Claude)",
  defaultModel: "claude-sonnet-4-5",
  modelHints: [
    "claude-haiku-4-5",
    "claude-sonnet-4-5",
    "claude-sonnet-4-6",
    "claude-opus-4-5",
    "claude-opus-4-7",
  ],

  async complete(req: ChatRequest, settings: LlmSettings): Promise<ChatResponse> {
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": settings.apiKey,
        "anthropic-version": "2023-06-01",
        // Required header for direct-from-browser calls.
        "anthropic-dangerous-direct-browser-access": "true",
      },
      body: JSON.stringify({
        model: settings.model,
        max_tokens: settings.maxTokens ?? DEFAULT_MAX_TOKENS,
        temperature: settings.temperature ?? DEFAULT_TEMPERATURE,
        system: req.system,
        messages: req.messages.map((m) => ({ role: m.role, content: m.content })),
      }),
    });

    if (!res.ok) {
      const body = await res.text();
      throw new LlmError(`Anthropic ${res.status}: ${body.slice(0, 200)}`, res.status);
    }

    const data = await res.json();
    const content: string = (data?.content ?? [])
      .filter((c: { type: string }) => c.type === "text")
      .map((c: { text: string }) => c.text)
      .join("\n");
    return {
      content,
      model: data?.model ?? settings.model,
      usage: {
        inputTokens: data?.usage?.input_tokens,
        outputTokens: data?.usage?.output_tokens,
      },
    };
  },
};

// ---------------------------------------------------------------------------
// Google Gemini — generateContent
// ---------------------------------------------------------------------------

const gemini: LlmProvider = {
  id: "gemini",
  label: "Google Gemini",
  defaultModel: "gemini-2.5-flash",
  modelHints: [
    "gemini-2.5-flash",
    "gemini-2.5-pro",
    "gemini-2.0-flash",
    "gemini-2.0-flash-lite",
  ],

  async complete(req: ChatRequest, settings: LlmSettings): Promise<ChatResponse> {
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(
      settings.model
    )}:generateContent?key=${encodeURIComponent(settings.apiKey)}`;

    // Gemini wants {role, parts:[{text}]} and uses 'model' for assistant turns.
    const contents = req.messages.map((m) => ({
      role: m.role === "assistant" ? "model" : "user",
      parts: [{ text: m.content }],
    }));

    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        systemInstruction: { parts: [{ text: req.system }] },
        contents,
        generationConfig: {
          temperature: settings.temperature ?? DEFAULT_TEMPERATURE,
          maxOutputTokens: settings.maxTokens ?? DEFAULT_MAX_TOKENS,
        },
      }),
    });

    if (!res.ok) {
      const body = await res.text();
      throw new LlmError(`Gemini ${res.status}: ${body.slice(0, 200)}`, res.status);
    }

    const data = await res.json();
    const parts = data?.candidates?.[0]?.content?.parts ?? [];
    const content: string = parts.map((p: { text?: string }) => p.text ?? "").join("\n");
    return {
      content,
      model: settings.model,
      usage: {
        inputTokens: data?.usageMetadata?.promptTokenCount,
        outputTokens: data?.usageMetadata?.candidatesTokenCount,
      },
    };
  },
};

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const PROVIDERS: Record<LlmProviderId, LlmProvider> = {
  openai,
  anthropic,
  gemini,
};

export function getProvider(id: LlmProviderId): LlmProvider {
  return PROVIDERS[id];
}

export const PROVIDER_LIST: LlmProvider[] = [openai, anthropic, gemini];
